/**
 * Stillness Detection for MediaPipe Landmarks
 * Detects static phases in golf swings to apply tighter smoothing
 */

export type XYV = { x: number; y: number; visibility?: number };
export type Landmarks = XYV[];

export interface StillnessCfg {
  anchors: ('leftShoulder' | 'rightShoulder' | 'leftHip' | 'rightHip')[];
  medianWindow: number;           // frames for velocity median
  vThreshPxPerS: number;          // velocity threshold for stillness
  minVisibility: number;          // minimum visibility to consider landmark
  hysteresisMs: number;           // avoid flapping between still/motion
}

type JointIndex = { [k: string]: number };

// MediaPipe Pose landmark indices
export const POSE_IDX: JointIndex = {
  leftShoulder: 11, rightShoulder: 12,
  leftHip: 23, rightHip: 24,
  leftElbow: 13, rightElbow: 14,
  leftWrist: 15, rightWrist: 16,
  leftKnee: 25, rightKnee: 26,
  leftAnkle: 27, rightAnkle: 28
};

function median(nums: number[]): number {
  if (!nums.length) return 0;
  const arr = nums.slice().sort((a, b) => a - b);
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}

export class StillnessDetector {
  private velHist: number[] = [];     // median velocity history (px/s)
  private lastLm?: Landmarks;
  private lastTime?: number;          // mediaTime (s)
  private lastChangeMs = 0;
  private isStillState = false;

  constructor(private cfg: StillnessCfg) {}

  step(lm: Landmarks, mediaTimeS: number, nowMs: number): { 
    isStill: boolean; 
    medianVel: number; 
    phase: 'warmup' | 'still' | 'motion' 
  } {
    if (!this.lastLm || this.lastTime === undefined) {
      this.lastLm = lm;
      this.lastTime = mediaTimeS;
      this.velHist.push(0);
      return { isStill: false, medianVel: 0, phase: 'warmup' };
    }

    // Calculate time delta and clamp for stability
    const dt = Math.max(1/240, Math.min(0.1, mediaTimeS - this.lastTime));
    
    // Calculate velocities for anchor joints
    const vels: number[] = [];
    for (const key of this.cfg.anchors) {
      const idx = POSE_IDX[key];
      const a = this.lastLm[idx];
      const b = lm[idx];
      
      const visOk = (a?.visibility ?? 1) >= this.cfg.minVisibility && 
                    (b?.visibility ?? 1) >= this.cfg.minVisibility;
      
      if (!a || !b || !visOk) continue;
      
      // Calculate pixel velocity
      const pixelDistance = Math.hypot(b.x - a.x, b.y - a.y);
      const velocity = pixelDistance / dt;
      vels.push(velocity);
    }

    // Update velocity history with median of anchor joints
    const currentVel = median(vels);
    this.velHist.push(currentVel);
    if (this.velHist.length > this.cfg.medianWindow) {
      this.velHist.shift();
    }

    // Calculate median velocity over window
    const medVel = median(this.velHist);

    // Determine if we want to be in still state
    const wantStill = medVel < this.cfg.vThreshPxPerS;

    // Apply hysteresis to prevent flapping
    if (wantStill !== this.isStillState && (nowMs - this.lastChangeMs) >= this.cfg.hysteresisMs) {
      this.isStillState = wantStill;
      this.lastChangeMs = nowMs;
      console.log(`[Stillness] State change: ${this.isStillState ? 'STILL' : 'MOTION'} (vel=${medVel.toFixed(1)}px/s)`);
    }

    this.lastLm = lm;
    this.lastTime = mediaTimeS;

    return { 
      isStill: this.isStillState, 
      medianVel: medVel,
      phase: this.isStillState ? 'still' : 'motion'
    };
  }

  reset(): void {
    this.velHist = [];
    this.lastLm = undefined;
    this.lastTime = undefined;
    this.lastChangeMs = 0;
    this.isStillState = false;
    console.log('[Stillness] Detector reset');
  }

  getStats(): {
    currentVelocity: number;
    isStill: boolean;
    historyLength: number;
  } {
    return {
      currentVelocity: this.velHist[this.velHist.length - 1] || 0,
      isStill: this.isStillState,
      historyLength: this.velHist.length
    };
  }
}
